<script setup lang="ts">
/**
 * スキルのカード 1 枚（#966）。スキル一覧（`SkillListDialog`）の左の列と、獲得のお知らせ（`SkillLearnedBody`）が共通で使う
 * （2026-10-07 doi: お知らせも一覧の左の列と同じ雰囲気で並べる）。
 *
 * 「条件分岐（if / elif / else）」のように末尾が（…）の名前は、名前と例の 2 行に分けて出す（2026-10-05 doi）。
 * 押せる一覧では `as="button"`、眺めるだけのお知らせでは既定の `div`。
 */
const props = withDefaults(defineProps<{
  label: string | null
  as?: 'button' | 'div'
  /** acquired = 手に入れた / named = まだだが名前は出す / far = 段にも届いていない / locked = 名前も出さない（「？」）/ hidden = 「？ あと N」 */
  state?: 'acquired' | 'named' | 'far' | 'locked' | 'hidden'
  selected?: boolean
}>(), { as: 'div', state: 'acquired', selected: false })

const parts = computed(() => {
  const m = props.label?.match(/^(.+?)(（[^（）]*）)$/)
  return m ? { name: m[1], syntax: m[2] } : { name: props.label ?? '', syntax: null }
})
</script>

<template>
  <component
    :is="as"
    :type="as === 'button' ? 'button' : undefined"
    :disabled="as === 'button' && state === 'locked' ? true : undefined"
    class="skill-chip"
    :class="[`skill-chip--${state}`, { 'skill-chip--selected': selected }]"
  >
    <span class="skill-chip__label">
      <template v-if="parts.syntax">
        {{ parts.name }}<br><span class="skill-chip__syntax">{{ parts.syntax }}</span>
      </template>
      <template v-else>{{ parts.name }}</template>
    </span>
  </component>
</template>

<style scoped>
.skill-chip {
  min-height: max(36px, calc(4.2 * var(--u)));
  padding: max(4px, calc(0.5 * var(--u))) max(6px, calc(0.8 * var(--u)));
  font: inherit;
  font-size: max(12px, calc(1.35 * var(--u)));
  line-height: 1.3;
  color: inherit;
  text-align: center;
  background: rgba(255, 250, 238, 0.85);
  border: 2px solid rgba(92, 62, 30, 0.35);
  border-radius: max(6px, calc(0.8 * var(--u)));
  transition: border-color 0.12s, transform 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
}

button.skill-chip {
  cursor: pointer;
}

/* 名前は最長 2 行（2026-10-05 doi）。はみ出す分は … にする（一覧では右の中身で全部読める） */
.skill-chip__label {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.skill-chip__syntax {
  font-size: 0.85em;
  opacity: 0.8;
}

button.skill-chip:hover,
button.skill-chip:focus-visible {
  border-color: rgba(92, 62, 30, 0.8);
}

.skill-chip--selected {
  border-color: #8a5a1c;
  background: #fff6dc;
  font-weight: bold;
}

/* まだのスキル。枠は同じで、中身が「？」なだけ */
.skill-chip--locked {
  cursor: default;
  background: rgba(92, 62, 30, 0.1);
  border-style: dashed;
  font-size: max(16px, calc(2 * var(--u)));
  opacity: 0.7;
}

/* 名前を出さないもの（技法・ジャンルのまだ）はまとめて 1 枚（隠し実績） */
.skill-chip--hidden {
  cursor: default;
  background: rgba(92, 62, 30, 0.1);
  border-style: dashed;
  opacity: 0.7;
}

/* まだの文法は名前を薄く（#956）。押すと名前と解放条件だけ見られる（#957） */
.skill-chip--named,
.skill-chip--far {
  background: rgba(92, 62, 30, 0.1);
  border-style: dashed;
  opacity: 0.5;
}

.skill-chip--named.skill-chip--selected {
  opacity: 0.85;
}

/* 段の経験値にもまだ届いていないもの（写経の対象でもない）は、さらに薄く */
.skill-chip--far {
  opacity: 0.32;
}
</style>
